import { apiClient, ApiResponse } from './api-client'
import { CampaignData, CreateCampaignData, UpdateCampaignData } from '../models/campaign'

export class CampaignService {
  /**
   * Get all campaigns for the authenticated business owner
   * @param filters - Optional filters for campaigns
   */
  async getCampaigns(filters?: {
    status?: 'all' | 'active' | 'inactive'
    startDate?: string
    endDate?: string
    sortBy?: {
      scans?: 'asc' | 'desc'
      visits?: 'asc' | 'desc'
      orders?: 'asc' | 'desc'
    }
  }): Promise<CampaignData[]> {
    const params = new URLSearchParams()
    
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status)
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate)
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate)
    }
    if (filters?.sortBy) {
      Object.entries(filters.sortBy).forEach(([key, value]) => {
        if (value) {
          params.append(`sortBy[${key}]`, value)
        }
      })
    }

    const queryString = params.toString()
    const url = queryString ? `/campaigns?${queryString}` : '/campaigns'
    
    const response = await apiClient.get<ApiResponse<{ campaigns: CampaignData[] }>>(url)
    if (response.data.success && response.data.data) {
      return response.data.data.campaigns || []
    }
    throw new Error(response.data.error?.message || 'Failed to get campaigns')
  }

  /**
   * Get campaign by ID
   */
  async getCampaign(id: number): Promise<CampaignData> {
    const response = await apiClient.get<ApiResponse<{ campaign: CampaignData }>>(`/campaigns/${id}`)
    if (response.data.success && response.data.data) {
      return response.data.data.campaign
    }
    throw new Error(response.data.error?.message || 'Failed to get campaign')
  }

  /**
   * Create new campaign
   */
  async createCampaign(data: CreateCampaignData): Promise<CampaignData> {
    const response = await apiClient.post<ApiResponse<{ campaign: CampaignData }>>('/campaigns', data)
    if (response.data.success && response.data.data) {
      return response.data.data.campaign
    }
    throw new Error(response.data.error?.message || 'Failed to create campaign')
  }

  /**
   * Update campaign
   */
  async updateCampaign(id: number, data: UpdateCampaignData): Promise<CampaignData> {
    const response = await apiClient.put<ApiResponse<{ campaign: CampaignData }>>(`/campaigns/${id}`, data)
    if (response.data.success && response.data.data) {
      return response.data.data.campaign
    }
    throw new Error(response.data.error?.message || 'Failed to update campaign')
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(id: number): Promise<void> {
    const response = await apiClient.delete<ApiResponse>(`/campaigns/${id}`)
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete campaign')
    }
  }
}

// Export singleton instance
export const campaignService = new CampaignService()

